// frontend/src/pages/HomePage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import API from '../services/api';
import { useApi } from '../hooks/useApi';
import Toast from '../components/ui/Toast';



function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [showMoreGallery, setShowMoreGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API Data Hooks - ADD THESE
  const { data: sliders, loading: slidersLoading } = useApi(() => API.content.getSliders());
  const { data: tours, loading: toursLoading } = useApi(() => API.tours.getFeatured());
  const { data: testimonials, loading: testimonialsLoading } = useApi(() => API.content.getTestimonials());
  const { data: gallery, loading: galleryLoading } = useApi(() => API.content.getGallery());
  const { data: settings } = useApi(() => API.settings.get());
  // Use API sliders or fallback
  const slideshowImages = (sliders || []).length > 0
    ? (sliders || []).map(slider => ({
      type: slider.video ? 'video' : 'image',
      src: slider.video_url || slider.image_url,
      title: slider.title,
      subtitle: slider.subtitle,
      description: slider.description,
    }))
    : [
      { type: 'image', src: '/pics/high-res/Beach and ATV.JPG' },
      { type: 'image', src: '/pics/new/71bd6041-fd9d-4b75-ab7c-9a9669860f5b.JPG' }
    ];

  // Slideshow State
  const [slideIndex, setSlideIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  const timerRef = useRef(null);
  const videoRefs = useRef({});

  // Slideshow Logic
  useEffect(() => {
    if (!slideshowImages || slideshowImages.length === 0) return;
    let duration = 6000;

    const currentSlide = slideshowImages[slideIndex];
    if (currentSlide.type === 'video') {
      const videoEl = videoRefs.current[slideIndex];
      if (videoEl) {
        videoEl.currentTime = 0;
        videoEl.play().catch(e => console.log("Autoplay prevented", e));
        if (videoEl.duration) {
          duration = videoEl.duration * 1000;
        } else {
          duration = 15000;
        }
      }
    }

    timerRef.current = setTimeout(() => {
      setPrevIndex(slideIndex);
      setSlideIndex((prev) => (prev + 1) % slideshowImages.length);
    }, duration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [slideIndex]);

  const handleVideoEnded = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPrevIndex(slideIndex);
    setSlideIndex((prev) => (prev + 1) % slideshowImages.length);
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Frontend validation
    if (formData.message.length < 10) {
      setToast({
        type: 'error',
        message: 'Please write a message with at least 10 characters.'
      });
      return;
    }

    setIsSubmitting(true); // ADD THIS LINE

    try {
      await API.contact.submit(formData);
      setToast({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      const errorMessage = error.message || 'Error sending message. Please try again.';
      setToast({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false); // ADD THIS LINE
    }
  }, [formData]);

  const activeSlide = slideshowImages[slideIndex] || {};

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Hero Section - Combined Design */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden reveal-on-scroll">

        {/* Slideshow Background */}
        {slideshowImages.map((slide, index) => {
          const isActive = index === slideIndex;

          return (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
            >
              {slide.type === 'video' ? (
                <video
                  ref={el => videoRefs.current[index] = el}
                  src={slide.src}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  onEnded={handleVideoEnded}
                  style={{ filter: 'brightness(0.6)' }}
                />
              ) : (
                <div className="w-full h-full relative overflow-hidden">
                  <img
                    src={slide.src}
                    alt="Hero Background"
                    className={`w-full h-full object-cover transition-transform duration-[8000ms] ease-linear ${isActive ? 'scale-110' : 'scale-100'
                      }`}
                    style={{ filter: 'brightness(0.6)' }}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Overlay Gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60 z-10 pointer-events-none"></div>

        {/* Main Content */}
        <div className="relative z-20 text-center text-white max-w-6xl mx-auto px-6 animate-fade-up flex flex-col justify-between h-full py-20">
          <div></div>

          {/* Centered Text Content */}
          <div>
            {/* Welcome to Layou Adventures */}
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase animate-slide-down">
                {activeSlide.subtitle || "St. Vincent's Premier ATV Tours"}
              </span>
            </div>

            <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight animate-slide-down transform transition-all duration-1000 drop-shadow-2xl text-white">
              {activeSlide.title || "Welcome to Layou Adventures"}
            </h1>

            {/* Description */}
            <p className="font-graphik text-lg md:text-xl lg:text-2xl mb-8 leading-relaxed max-w-4xl mx-auto animate-fade-in delay-600 transform transition-all duration-1000 text-white/90 drop-shadow-lg">
              {activeSlide.description || "Explore St. Vincent's hidden trails and breathtaking landscapes. Guided ATV tours • Hikes • Waterfall adventures, all expertly crafted for the curious adventurer."}            </p>
          </div>

          {/* Call to Action Buttons - at bottom */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-bounce-gentle delay-900">
            <Link to="/tours" className="bg-emerald-600 hover:bg-emerald-700 text-white font-graphik font-semibold px-8 py-4 rounded-full transition-all duration-500 transform hover:scale-105 hover:shadow-xl flex items-center justify-center space-x-2 min-w-[200px] border border-black/20">
              <span>View Tours & Book</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </Link>

            <a href="#socials" className="border-2 border-white/60 hover:border-white text-white hover:bg-white/10 font-graphik font-semibold px-8 py-4 rounded-full transition-all duration-500 transform hover:scale-105 flex items-center justify-center space-x-2 backdrop-blur-sm min-w-[200px]">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>See Experiences</span>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Image Layout */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-white dark:from-gray-900 dark:to-gray-800 reveal-on-scroll">
        <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 xl:px-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image Side */}
            <div className="relative group">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group-hover:shadow-3xl transition-all duration-700 group-hover:scale-[1.02]">
                <ResponsiveImage
                  src="/pics/new/IMG_4265.jpg"
                  alt="Professional ATV Guide Leading Adventure Tour"
                  className="w-full h-96 object-cover transition-transform duration-1000 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent transition-opacity duration-500 group-hover:opacity-75"></div>

                {/* Floating Stats Card */}
                <div className="absolute bottom-6 left-6 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-500 hover:scale-110 hover:-translate-y-2 group cursor-pointer">
                  <div className="flex items-center space-x-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300 group-hover:scale-110 transform">500+</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">Happy Adventurers</div>
                    </div>
                    <div className="w-px h-12 bg-gray-300 dark:bg-gray-600 group-hover:bg-emerald-300 dark:group-hover:bg-emerald-600 transition-colors duration-300"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors duration-300 group-hover:scale-110 transform">4.9★</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">Average Rating</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gold/20 rounded-full blur-xl animate-pulse group-hover:scale-125 transition-transform duration-700"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-emerald-200/30 dark:bg-emerald-700/30 rounded-full blur-2xl animate-pulse delay-1000 group-hover:scale-110 transition-transform duration-1000"></div>
            </div>

            {/* Content Side */}
            <div className="space-y-8">
              <div className="section-header text-center">
                <div className="section-overline mb-4">
                  <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                    Why Choose Layou Adventures
                  </span>
                </div>
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                  Experience St. Vincent Like Never Before
                </h2>
                <p className="font-graphik text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                  We're not just another tour company. We're your local adventure partners, passionate about showing you the hidden gems and authentic experiences that make St. Vincent truly special.
                </p>

                {/* Decorative geometric patterns */}
                <div className="absolute inset-0 -z-10 opacity-10 dark:opacity-5">
                  <div className="absolute top-10 right-10 w-24 h-24 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full blur-xl"></div>
                  <div className="absolute bottom-10 left-10 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-xl"></div>
                </div>
              </div>

              {/* Feature List */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4 group hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 p-4 rounded-xl transition-all duration-300 hover:scale-105 hover:-translate-y-1">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800 group-hover:scale-110 transition-all duration-300 shadow-lg">
                    <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-graphik font-bold text-lg text-emerald-800 dark:text-white mb-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-200 transition-colors duration-300">Expert Local Guides</h3>
                    <p className="text-gray-600 dark:text-gray-300 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300">Born and raised in St. Vincent, our guides know every trail, waterfall, and hidden viewpoints.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 p-4 rounded-xl transition-all duration-300 hover:scale-105 hover:-translate-y-1">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800 group-hover:scale-110 transition-all duration-300 shadow-lg">
                    <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-graphik font-bold text-lg text-emerald-800 dark:text-white mb-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-200 transition-colors duration-300">Safety First Approach</h3>
                    <p className="text-gray-600 dark:text-gray-300 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300">Premium insured ATVs, comprehensive safety briefings with qualified and knowledgeable guides.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 group hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 p-4 rounded-xl transition-all duration-300 hover:scale-105 hover:-translate-y-1">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 dark:bg-emerald-900 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800 group-hover:scale-110 transition-all duration-300 shadow-lg">
                    <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-graphik font-bold text-lg text-emerald-800 dark:text-white mb-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-200 transition-colors duration-300">Personalized Experiences</h3>
                    <p className="text-gray-600 dark:text-gray-300 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300">Small groups and custom itineraries tailored to your interests and adventure level.</p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-6">
                <Link
                  to="/tours"
                  className="inline-flex items-center bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl hover:shadow-emerald-500/30 space-x-3 border border-black/20 group"
                >
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Start Your Adventure</span>
                  <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-20 bg-emerald-50 dark:bg-gray-900 reveal-on-scroll">
        <div className="container mx-auto px-8 md:px-12 lg:px-16 relative">
          <div className="section-header text-center mb-16">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Featured Experiences
              </span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">What We Offer</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Discover our handcrafted adventures designed to showcase the very best of St.&nbsp;Vincent's natural beauty and culture.
            </p>

            {/* Decorative geometric patterns */}
            <div className="absolute inset-0 -z-10 opacity-10 dark:opacity-5">
              <div className="absolute top-10 left-10 w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full blur-xl"></div>
              <div className="absolute bottom-10 right-10 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-xl"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {toursLoading ? (
              <div className="col-span-3 text-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : tours && tours.length > 0 ? (
              tours.slice(0, 3).map((tour) => (
                <div key={tour.id} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="relative">
                    <ResponsiveImage
                      src={tour.image_url || '/pics/default-tour.jpg'}
                      alt={tour.name}
                      className="w-full h-64 object-cover"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                    <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {tour.duration}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <h3 className="font-playfair text-xl font-bold text-gray-900 dark:text-white mb-1">
                        {tour.name}
                      </h3>
                      {tour.subtitle && (
                        <p className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold">
                          {tour.subtitle}
                        </p>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                      {tour.description}
                    </p>

                    {tour.includes && tour.includes.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-2">Includes:</h4>
                        <div className="grid grid-cols-2 gap-1">
                          {tour.includes.slice(0, 4).map((item, i) => (
                            <div key={i} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                              <span className="text-xs text-gray-600 dark:text-gray-400">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {tour.tags && tour.tags.length > 0 && (
                      <div className="mb-6">
                        <div className="flex flex-wrap gap-2">
                          {tour.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-md text-xs font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400">From</span>
                          <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            ${tour.price_single_atv || tour.price}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">USD</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Single ATV • Double: ${tour.price_double_atv || (tour.price * 1.5).toFixed(0)}
                        </p>
                      </div>
                      <Link
                        to={`/tours/${tour.slug}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm border border-black/20"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-20">
                <p className="text-gray-600">No tours available</p>
              </div>
            )}
          </div>

          {/* Additional Features */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h4 className="font-bold text-emerald-800 dark:text-white">Small Groups</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Intimate experiences with personalized attention</p>
            </div>

            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-bold text-emerald-800 dark:text-white">Flexible Timing</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Tours scheduled around your convenience</p>
            </div>

            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-bold text-emerald-800 dark:text-white">Safety First</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Professional equipment and certified guides</p>
            </div>

            <div className="space-y-4">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h4 className="font-bold text-emerald-800 dark:text-white">Local Expertise</h4>
              <p className="text-gray-600 dark:text-gray-300 text-sm">Native guides sharing authentic island culture</p>
            </div>
          </div>
        </div>
      </section>

      {/* Adventure Photo Gallery */}
      <section className="py-20 bg-gray-50 reveal-on-scroll">
        <div className="container mx-auto px-8 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Adventure Gallery
            </h2>
            <p className="font-graphik text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Discover the breathtaking landscapes and thrilling moments from our ATV adventures. Every ride tells a story of exploration and excitement.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {galleryLoading ? (
              <div className="col-span-4 text-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : gallery && gallery.length > 0 ? (
              gallery.slice(0, showMoreGallery ? gallery.length : 12).map((image) => (
                <div
                  key={image.id}
                  className="group relative overflow-hidden rounded-xl shadow-lg cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl"
                  onClick={() => setSelectedImage(image)}
                >
                  <ResponsiveImage
                    src={image.image_url}
                    alt={image.title || 'Gallery image'}
                    className="w-full h-40 md:h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                    sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <h4 className="font-graphik font-bold text-white text-sm mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">{image.title}</h4>
                      <p className="font-graphik text-gray-200 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300">{image.description}</p>
                    </div>
                  </div>

                  {/* Hover overlay icon */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center py-20">
                <p className="text-gray-600">No gallery images available</p>
              </div>
            )}
          </div>

          {/* View More Button */}
          <div className="text-center mt-8">
            <button
              onClick={() => setShowMoreGallery(!showMoreGallery)}
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-graphik font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2 border border-black/20"
            >
              <span>{showMoreGallery ? 'Show Less Photos' : 'View More Photos'}</span>
              <svg
                className={`w-5 h-5 transition-transform duration-300 ${showMoreGallery ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-20 bg-white reveal-on-scroll">
        <div className="container mx-auto px-8 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
                  About Us
                </h2>
                <p className="font-graphik text-lg text-gray-700 leading-relaxed mb-4">
                  At LayouAdventures, we believe travel is more than just a destination, it’s a journey filled with stories, culture, and connection. Whether you're chasing waterfalls, wandering through vibrant villages, or seeking the peace of remote beaches, we craft travel experiences that stay with you forever.
                </p>
                <p className="font-graphik text-lg text-gray-700 leading-relaxed">
                  Our expert team curates adventures tailored to your style, so whether you're a solo explorer, a couple, or a group of thrill-seekers, we’ve got your perfect itinerary.
                </p>
              </div>
            </div>

            <div className="relative">
              <ResponsiveImage
                src="/about.png"
                alt="ATV Adventure in St. Vincent"
                className="rounded-3xl shadow-md w-full h-96 object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />

              {/* Badges removed as requested */}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Continuous Scrolling */}
      <section className="py-16 bg-emerald-50 dark:bg-gray-900 reveal-on-scroll overflow-hidden">
        <div className="container mx-auto px-8 md:px-12 lg:px-16 relative">
          <div className="section-header text-center mb-12">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Real Adventures
              </span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-4">
              What Our Adventurers Say
            </h2>
            <p className="font-graphik text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Real experiences from travelers who've discovered St. Vincent with us.
            </p>

            {/* Decorative geometric patterns */}
            <div className="absolute inset-0 -z-10 opacity-10 dark:opacity-5">
              <div className="absolute top-5 right-20 w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full blur-lg"></div>
              <div className="absolute bottom-5 left-20 w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-lg"></div>
            </div>
          </div>

          {/* Continuous Scrolling Container */}
          <div className="relative">
            <div className="flex animate-scroll-left space-x-6">
              {testimonialsLoading ? (
                <div className="flex-shrink-0 w-80 bg-white dark:bg-gray-800 rounded-2xl p-6">
                  <div className="animate-pulse h-32"></div>
                </div>
              ) : testimonials && testimonials.length > 0 ? (
                <>
                  {/* First set */}
                  {testimonials.map((testimonial) => (
                    <div key={testimonial.id} className="flex-shrink-0 w-80 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                      <div className="flex items-center mb-3">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="font-graphik text-gray-700 dark:text-gray-300 mb-4 text-sm leading-relaxed">
                        "{testimonial.testimonial}"
                      </p>
                      <div>
                        <h4 className="font-graphik font-bold text-emerald-800 dark:text-white text-sm">
                          {testimonial.customer_name}
                        </h4>
                        <p className="font-graphik text-gray-600 dark:text-gray-400 text-xs">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {testimonials.map((testimonial) => (
                    <div key={`dup-${testimonial.id}`} className="flex-shrink-0 w-80 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
                      <div className="flex items-center mb-3">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <svg key={i} className="w-4 h-4 text-gold" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <p className="font-graphik text-gray-700 dark:text-gray-300 mb-4 text-sm leading-relaxed">
                        "{testimonial.testimonial}"
                      </p>
                      <div>
                        <h4 className="font-graphik font-bold text-emerald-800 dark:text-white text-sm">
                          {testimonial.customer_name}
                        </h4>
                        <p className="font-graphik text-gray-600 dark:text-gray-400 text-xs">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Socials & Gallery Section */}
      <section id="socials" className="py-16 bg-gradient-to-b from-gray-900 to-black text-white reveal-on-scroll">
        <div className="container mx-auto px-8 md:px-12 lg:px-16 relative">
          {/* Header */}
          <div className="section-header text-center mb-12">
            <div className="section-overline mb-4">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Stay Connected
              </span>
            </div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-4">
              Follow Our Adventures
            </h2>
            <p className="font-graphik text-xl text-gray-300 max-w-2xl mx-auto">
              Stay connected with us and explore our latest adventures through photos and videos
            </p>

            {/* Decorative geometric patterns */}
            <div className="absolute inset-0 -z-10 opacity-10">
              <div className="absolute top-5 left-10 w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full blur-xl"></div>
              <div className="absolute bottom-5 right-10 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-xl"></div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
            {/* Social Media Links - Enhanced */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-xl border border-gray-700">
                <h3 className="font-playfair text-2xl font-bold text-white mb-6">Connect With Us</h3>

                {/* Instagram */}
                <a href="https://www.instagram.com/layouadventures/" target="_blank" rel="noopener noreferrer" className="block">
                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-pink-500/20 hover:to-purple-500/20 transition-all duration-300 cursor-pointer mb-4">
                    <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-white group-hover:text-pink-300 transition-colors">Instagram</p>
                      <p className="font-graphik text-gray-300 text-sm">@layouadventures</p>
                      <p className="font-graphik text-gray-400 text-xs">Daily adventure photos</p>
                    </div>
                  </div>
                </a>

                {/* Facebook */}
                <a href="https://www.facebook.com/p/Layou-Adventures-61568438644995/" target="_blank" rel="noopener noreferrer" className="block">
                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-blue-500/20 transition-all duration-300 cursor-pointer mb-4">
                    <div className="bg-blue-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-white group-hover:text-blue-300 transition-colors">Facebook</p>
                      <p className="font-graphik text-gray-300 text-sm">Layou Adventures</p>
                      <p className="font-graphik text-gray-400 text-xs">Community & updates</p>
                    </div>
                  </div>
                </a>

                {/* YouTube */}
                <a href="https://www.youtube.com/watch?v=SLc7qmWD4G8" target="_blank" rel="noopener noreferrer" className="block">
                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-red-500/20 transition-all duration-300 cursor-pointer mb-4">
                    <div className="bg-red-600 p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-white group-hover:text-red-300 transition-colors">YouTube</p>
                      <p className="font-graphik text-gray-300 text-sm">Layou Adventures</p>
                      <p className="font-graphik text-gray-400 text-xs">Adventure videos</p>
                    </div>
                  </div>
                </a>

                {/* TikTok */}
                <a href="https://www.tiktok.com/@layouadventuresatvs1" target="_blank" rel="noopener noreferrer" className="block">
                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-gray-500/20 transition-all duration-300 cursor-pointer">
                    <div className="bg-black p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300 border border-gray-600">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-white group-hover:text-gray-300 transition-colors">TikTok</p>
                      <p className="font-graphik text-gray-300 text-sm">@layouadventuresatvs1</p>
                      <p className="font-graphik text-gray-400 text-xs">Short adventure clips</p>
                    </div>
                  </div>
                </a>
              </div>

              {/* Featured Video */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-xl border border-gray-700">
                <h4 className="font-playfair text-xl font-bold text-white mb-4">Latest Adventure</h4>
                <div className="w-full rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    title="Layou Adventures Highlight"
                    className="w-full h-48 md:h-56"
                    src="https://www.youtube.com/embed/SLc7qmWD4G8?autoplay=1&mute=1&loop=1&playlist=SLc7qmWD4G8&controls=1&rel=0&modestbranding=1&playsinline=1"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Instagram Posts Grid */}
            <div className="lg:col-span-3">
              <h4 className="font-playfair text-2xl font-bold text-white mb-6 text-center">Recent Adventures</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    image: '/pics/new/IMG_4521.JPG',
                    caption: 'Solo rider exploring the jungle trails! 🌿 Perfect weather for an adventure today. #LayouAdventures #ATVLife #StVincent',
                    likes: 127,
                    comments: 23,
                    timeAgo: '2 hours ago'
                  },
                  {
                    image: '/pics/high-res/ATV and Forest 2.JPG',
                    caption: 'Through the emerald canopy we ride! 🍃 Nature therapy at its finest. Who wants to join us tomorrow? #RainforestRide #Adventure',
                    likes: 89,
                    comments: 15,
                    timeAgo: '6 hours ago'
                  },
                  {
                    image: '/pics/new/IMG_4297.jpg',
                    caption: 'Pirates of the Caribbean filming location! 🏴‍☠️ Experience where movie magic happened. Book your tour today! #PiratesLocation #MovieTours',
                    likes: 203,
                    comments: 41,
                    timeAgo: '1 day ago'
                  },
                  {
                    image: '/pics/new/IMG_4337.jpg',
                    caption: 'Double the fun, double the adventure! 👫 Perfect for couples and friends. Safety and smiles guaranteed! #DoubleRider #SafetyFirst',
                    likes: 156,
                    comments: 28,
                    timeAgo: '2 days ago'
                  },
                  {
                    image: '/pics/high-res/View of Layou.JPG',
                    caption: 'The breathtaking Layou Valley from above! 🏔️ This view never gets old. Come see it for yourself! #LayouValley #ScenicViews',
                    likes: 245,
                    comments: 67,
                    timeAgo: '3 days ago'
                  },
                  {
                    image: '/pics/high-res/Rain Forest view 1.JPG',
                    caption: 'Lost in the green paradise 🌲✨ The rainforest holds so many secrets. What will you discover? #RainforestAdventure #NatureLovers',
                    likes: 178,
                    comments: 34,
                    timeAgo: '4 days ago'
                  }
                ].map((post, i) => (
                  <div key={i} className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-pink-500/20">
                    {/* Post Header */}
                    <div className="p-4 border-b border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.405.042-3.441.219-.937 1.404-5.965 1.404-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.888-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.357-.631-2.747-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-graphik font-bold text-white text-sm">layouadventures</p>
                          <p className="font-graphik text-gray-400 text-xs">St. Vincent & The Grenadines</p>
                        </div>
                      </div>
                    </div>

                    {/* Post Image */}
                    <div className="relative aspect-square overflow-hidden">
                      <ResponsiveImage
                        src={post.image}
                        alt="Adventure post"
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Post Actions */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-4">
                          <button className="hover:scale-110 transition-transform duration-200">
                            <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                          </button>
                          <button className="hover:scale-110 transition-transform duration-200">
                            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </button>
                          <button className="hover:scale-110 transition-transform duration-200">
                            <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </button>
                        </div>
                        <button className="hover:scale-110 transition-transform duration-200">
                          <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                          </svg>
                        </button>
                      </div>

                      <div className="text-white">
                        <p className="font-graphik font-semibold text-sm mb-1">{post.likes.toLocaleString()} likes</p>
                        <p className="font-graphik text-sm mb-2">
                          <span className="font-semibold">layouadventures</span> {post.caption}
                        </p>
                        <p className="font-graphik text-gray-400 text-xs mb-1">View all {post.comments} comments</p>
                        <p className="font-graphik text-gray-500 text-xs uppercase">{post.timeAgo}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* View Instagram Profile Button */}
              <div className="text-center mt-8">
                <a href="https://www.instagram.com/layouadventures/" target="_blank" rel="noopener noreferrer">
                  <button className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white font-graphik font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z" />
                    </svg>
                    <span>Follow @layouadventures</span>
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Contact Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-gray-800 dark:text-gray-200 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute top-10 left-10 w-24 h-24 bg-emerald-600 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-16 h-16 bg-gold rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-12 h-12 bg-emerald-400 rounded-full"></div>
        </div>

        <div className="container mx-auto px-8 md:px-12 lg:px-16 relative z-10">
          <div className="section-header text-center mb-16">
            <div className="section-overline mb-6">
              <span className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 bg-clip-text text-transparent font-bold tracking-widest text-sm uppercase">
                Get In Touch
              </span>
            </div>
            <h2 className="font-playfair text-4xl md:text-6xl font-bold mb-6 text-emerald-800 dark:text-white">
              Ready for Your Next
              <span className="block text-gold">Adventure?</span>
            </h2>
            <p className="font-graphik text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Contact us today to book your unforgettable St. Vincent adventure. Our team is standing by to help you create memories that will last a lifetime.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Contact Information & Quick Links */}
            <div className="space-y-8">
              {/* Main Contact Card */}
              <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-2xl border border-emerald-100 dark:border-gray-700 transform hover:scale-105 transition-all duration-300">
                <h3 className="font-playfair text-2xl font-bold mb-6 text-emerald-800 dark:text-white">Contact Information</h3>

                <div className="space-y-6">
                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-emerald-50 dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-lg text-emerald-800 dark:text-white">Call Us Now</p>
                      <p className="font-graphik text-gray-600 dark:text-gray-300 text-lg hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">{settings?.contact_phone || '(614) 385-2846'}</p>
                      <p className="font-graphik text-gray-500 dark:text-gray-400 text-sm">{settings?.business_hours_mon_fri || 'Mon-Fri 9AM-5PM'} , {settings?.business_hours_saturday || 'Sat 10AM-5PM'}</p>
                    </div>
                  </div>

                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-emerald-50 dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-lg text-emerald-800 dark:text-white">Email Us</p>
                      <p className="font-graphik text-gray-600 dark:text-gray-300 text-lg hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">{settings?.contact_email || 'info@layouadventures.com'}</p>
                      <p className="font-graphik text-gray-500 dark:text-gray-400 text-sm">Quick response guaranteed</p>
                    </div>
                  </div>

                  <div className="group flex items-center space-x-4 p-4 rounded-xl hover:bg-emerald-50 dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-graphik font-bold text-lg text-emerald-800 dark:text-white">Visit Us</p>
                      <p className="font-graphik text-gray-600 dark:text-gray-300 text-lg hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">{settings?.site_address || 'Layou Valley St. Vincent and the Grenadines'}</p>
                      <p className="font-graphik text-gray-500 dark:text-gray-400 text-sm">Caribbean paradise awaits</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a href="tel:+16143852846" className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-graphik font-bold px-6 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Call Now</span>
                </a>

                <a href="https://wa.me/12489796051" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-emerald-900 font-graphik font-bold px-6 py-4 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>WhatsApp</span>
                </a>
              </div>

              {/* Business Hours */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-emerald-100 dark:border-gray-700">
                <h4 className="font-playfair text-xl font-bold text-emerald-800 dark:text-white mb-4">Business Hours</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-600">
                    <span className="font-graphik text-gray-700 dark:text-gray-300">Monday - Friday</span>
                    <span className="font-graphik font-semibold text-emerald-600 dark:text-emerald-400">{settings?.business_hours_mon_fri || 'Mon-Fri 9AM-5PM'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-600">
                    <span className="font-graphik text-gray-700 dark:text-gray-300">Saturday</span>
                    <span className="font-graphik font-semibold text-emerald-600 dark:text-emerald-400">{settings?.business_hours_saturday || 'Sat 10AM-5PM'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="font-graphik text-gray-700 dark:text-gray-300">Sunday</span>
                    <span className="font-graphik font-semibold text-emerald-600 dark:text-emerald-400">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Compact Contact Form */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-emerald-100 dark:border-gray-700">
              <div className="text-center mb-6">
                <h3 className="font-playfair text-2xl font-bold text-emerald-800 dark:text-white mb-2">Send us a Message</h3>
                <p className="font-graphik text-gray-600 dark:text-gray-300 text-sm">Tell us about your dream adventure</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-graphik font-medium mb-2 text-gray-800 dark:text-gray-200">First Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors"
                      placeholder="Full Name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-graphik font-medium mb-2 text-gray-800 dark:text-gray-200">Phone </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors"
                      placeholder="Phone Number (optional)"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-graphik font-medium mb-2 text-gray-800 dark:text-gray-200">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block font-graphik font-medium mb-2 text-gray-800 dark:text-gray-200">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors resize-none"
                    placeholder="Tell us about your dream adventure..."
                    minLength="10"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-graphik font-semibold px-6 py-2 rounded-full transition-colors w-full disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>

            </div>
          </div>
        </div>
      </section>

      {/* Image Expansion Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedImage.image_url || selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <h3 className="text-white font-bold text-xl mb-2">{selectedImage.title}</h3>
              <p className="text-gray-300">{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HomePage;